import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    getSupplierActivationStatus,
    shouldShowEnableProductOrderingButton,
} from 'entities/CatalogItem';
import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { propertyActions } from 'entities/Property/model/slices/PropertySlice';
import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { UserRole, getUserRole } from 'entities/User';
import { getPropertyVendorsMap } from 'entities/Vendors';
import {
    getRouteAddVendors,
    getRouteVendorsSupplierPage,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

export const useSupplierActions = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const vendorsMap = useSelector(getPropertyVendorsMap);
    const currentUserRole = useSelector(getUserRole);

    const canManageVendors = useMemo(
        () =>
            [UserRole.PMC_ADMIN, UserRole.PROPERTY_MANAGER].includes(
                currentUserRole,
            ),
        [currentUserRole],
    );

    // NOTE: Renovations are not currently supported. When renovations feature
    // is added, uncomment this line and update the logic below to handle
    // renovationsAccountStatus checks.
    // const renovationEnabled = useFeatureFlag('renovation_enabled') || false;
    const handleEnableProductOrdering = useCallback(
        (proposal: Proposal) => {
            const vendor = vendorsMap?.[proposal.seller.uid];
            if (selectedPropertyId && vendor?.id) {
                // Vendor in list but not verified - navigate to supplier page and open enable product ordering modal
                navigate(
                    getRouteVendorsSupplierPage(vendor.id, selectedPropertyId),
                    { state: { openEnableProductOrdering: true } },
                );
            } else if (!vendor) {
                // Vendor not in list - navigate to Add Vendor page and open add-vendor modal for this supplier
                if (selectedPropertyId) {
                    dispatch(
                        propertyActions.setPropertyForVendor(
                            selectedPropertyId,
                        ),
                    );
                    navigate(getRouteAddVendors(selectedPropertyId), {
                        state: { openVendorByUid: proposal.seller.uid },
                    });
                }
            }
        },
        [dispatch, vendorsMap, navigate, selectedPropertyId],
    );

    const getProposalStatus = useCallback(
        (proposal: Proposal) => {
            const activationStatus = getSupplierActivationStatus(
                proposal,
                vendorsMap,
            );
            const showEnableButton =
                canManageVendors &&
                shouldShowEnableProductOrderingButton(proposal, vendorsMap);
            const vendor = vendorsMap?.[proposal.seller.uid];

            return { activationStatus, showEnableButton, vendor };
        },
        [vendorsMap, canManageVendors],
    );

    return {
        vendorsMap,
        canManageVendors,
        handleEnableProductOrdering,
        getProposalStatus,
    };
};
